/**
 * Public write-ops facade — every call site in the PWA uses this.
 *
 *   const visitLocalId = await writeOps.checkIn.apply({
 *     storeId, projectId, gps, odometer, dteCall, stCallTime, associateId, storeName,
 *   });
 *
 * The facade:
 *   1. Mints a new localId (v4 UUID) — the canonical client-side ID.
 *   2. Calls op.applyLocal so render IDB rows reflect the change
 *      immediately (rep's UI never blocks on the network).
 *   3. Enqueues the serialized op into visit_sync_queue.
 *   4. Dispatches `rdm:sync-enqueued` to wake the sync engine.
 *
 * The sync engine (Task 1.8) drains via OP_REGISTRY[row.action].sync(row).
 */

import { v4 as uuidv4 } from 'uuid';
import type { WriteOp, WriteOpFacade, LocalId, QueueRow } from './types';
import { STORES, put, getAll } from '../utils/indexedDB';
import { checkInOp } from './ops/checkIn';
import { checkOutOp } from './ops/checkOut';
import { patchReturnDriveOp } from './ops/patchReturnDrive';
import { patchOdometerOp } from './ops/patchOdometer';
import { voidCallLogOp } from './ops/voidCallLog';
import { reopenVisitOp } from './ops/reopenVisit';
import { deleteTimeEntryOp } from './ops/deleteTimeEntry';
import { editVisitOp } from './ops/editVisit';
import { submitSurveyOp } from './ops/submitSurvey';
import { uploadPhotoOp } from './ops/uploadPhoto';
import { uploadDocumentOp } from './ops/uploadDocument';
import { addStoreNoteOp } from './ops/addStoreNote';
import { addStoreManagerOp } from './ops/addStoreManager';
import { createOrderOp } from './ops/createOrder';
import { createManualVisitOp } from './ops/createManualVisit';
import { patchStoreCoordinatesOp } from './ops/patchStoreCoordinates';
import { uploadProfileSignatureOp } from './ops/uploadProfileSignature';

export const SYNC_ENQUEUED_EVENT = 'rdm:sync-enqueued';

/**
 * Canonical registry of every write op. Keyed by `kind`. The sync
 * engine uses this to dispatch drain calls; tests use it to look up an
 * op for assertion.
 */
export const OP_REGISTRY = {
  checkIn: checkInOp,
  checkOut: checkOutOp,
  patchReturnDrive: patchReturnDriveOp,
  patchOdometer: patchOdometerOp,
  voidCallLog: voidCallLogOp,
  reopenVisit: reopenVisitOp,
  deleteTimeEntry: deleteTimeEntryOp,
  editVisit: editVisitOp,
  submitSurvey: submitSurveyOp,
  uploadPhoto: uploadPhotoOp,
  uploadDocument: uploadDocumentOp,
  addStoreNote: addStoreNoteOp,
  addStoreManager: addStoreManagerOp,
  createOrder: createOrderOp,
  createManualVisit: createManualVisitOp,
  patchStoreCoordinates: patchStoreCoordinatesOp,
  uploadProfileSignature: uploadProfileSignatureOp,
} as const;

export type OpKind = keyof typeof OP_REGISTRY;

/**
 * Get the op registered under `kind`. Returns null for unknown kinds
 * — the sync engine treats unknowns as terminal failures.
 */
export function getOp(kind: string): WriteOp<unknown, unknown, unknown> | null {
  const op = (OP_REGISTRY as Record<string, WriteOp<unknown, unknown, unknown> | undefined>)[
    kind
  ];
  return op ?? null;
}

/**
 * Mint a new localId. Wrapped so a future scheme swap (e.g. ULIDs)
 * touches one line.
 */
export function newLocalId(): LocalId {
  return uuidv4();
}

/**
 * Resolve a parent visit's localId (its visit_task_sessions `uuid`)
 * from a known server `callLogId`.
 *
 * Used by dual-write call sites (Phase 1 of #1983/#1985) that only hold
 * the server id but need the parent localId to enqueue a dependent op
 * (checkOut, submitSurvey, uploadPhoto, ...). Returns null when no local
 * row matches — the caller then skips the enqueue (Phase 1 fail-safe:
 * the direct fetch already performed the operation, so skipping the
 * offline backup is harmless). As check-ins flow through the new
 * dual-write path, local rows accumulate and coverage grows.
 */
export async function resolveVisitLocalId(
  callLogId: number | null | undefined,
): Promise<LocalId | null> {
  if (callLogId == null) return null;
  const rows = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<{
    uuid?: string;
    callLogId?: number | null;
  }>;
  const match = rows.find((r) => r.callLogId === callLogId);
  return match?.uuid ?? null;
}

/**
 * Adopt a SERVER-SOURCED CallLog into the local visit_task_sessions store
 * so the write queue can act on it (#1987).
 *
 * Some write sites operate on rows that originated on the server (e.g.
 * deleting or editing a past CallLog fetched from the API) and therefore
 * have NO local visit_task_sessions row. `resolveVisitLocalId(callLogId)`
 * returns null for those, so dependent queue ops can't resolve a parent
 * localId. This primitive creates (or finds) a local row carrying the
 * server `callLogId`, returning its uuid so the caller can rekey its
 * dependent writes through the queue.
 *
 * Idempotent on `callLogId`: if a row already references it, that row's
 * uuid is returned and no duplicate is created. The created row is stamped
 * status 'completed' — it represents an already-synced server visit.
 */
export async function adoptServerCallLog(
  callLogId: number,
  meta?: {
    storeId?: number;
    projectId?: number;
    storeName?: string;
    taskType?: 'set' | 'call';
    taskId?: number;
    projectDescription?: string;
  },
): Promise<LocalId> {
  const existing = await resolveVisitLocalId(callLogId);
  if (existing) return existing;

  const localId = newLocalId();
  const row: Record<string, unknown> = {
    uuid: localId,
    callLogId,
    status: 'completed',
  };
  if (meta?.storeId !== undefined) row.storeId = meta.storeId;
  if (meta?.projectId !== undefined) row.projectId = meta.projectId;
  if (meta?.storeName !== undefined) row.storeName = meta.storeName;
  if (meta?.taskType !== undefined) row.taskType = meta.taskType;
  if (meta?.taskId !== undefined) row.taskId = meta.taskId;
  if (meta?.projectDescription !== undefined) row.projectDescription = meta.projectDescription;

  await put(STORES.VISIT_TASK_SESSIONS, row);
  return localId;
}

/**
 * Low-level enqueue that reuses an explicit `localId` as both the local
 * row key and the Idempotency-Key. Use this (not the facade) when a
 * call site already has a stable idempotency key it must share with an
 * existing direct fetch — e.g. check-in's `checkInKey` — so the server
 * dedups on the duplicate POST. Falls back to a fresh localId when none
 * is supplied.
 */
export async function enqueueOp<TInput>(
  kind: OpKind,
  input: TInput,
  localId: LocalId = newLocalId(),
): Promise<LocalId> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const op = OP_REGISTRY[kind] as unknown as WriteOp<TInput, any, any>;
  await op.applyLocal(input, localId);
  const serialized = op.serialize(input, localId);
  await put(STORES.VISIT_SYNC_QUEUE, {
    ...serialized,
    status: 'pending',
    retries: 0,
    createdAt: new Date().toISOString(),
  });
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event(SYNC_ENQUEUED_EVENT));
  }
  return localId;
}

// `any` for TPayload/TResponse — the registry is a heterogeneous map of
// ops with different payload/response shapes; the facade only uses
// applyLocal + serialize, both of which are parameterized solely by
// TInput. Variance forces `any` here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function makeFacade<TInput>(op: WriteOp<TInput, any, any>): WriteOpFacade<TInput> {
  return {
    async apply(input: TInput): Promise<LocalId> {
      const localId = newLocalId();
      await op.applyLocal(input, localId);
      const serialized = op.serialize(input, localId);
      const queueRow: Omit<QueueRow, 'id'> = {
        ...serialized,
        status: 'pending',
        retries: 0,
        createdAt: new Date().toISOString(),
      };
      await put(STORES.VISIT_SYNC_QUEUE, queueRow);
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event(SYNC_ENQUEUED_EVENT));
      }
      return localId;
    },
  };
}

export const writeOps = {
  checkIn: makeFacade(checkInOp),
  checkOut: makeFacade(checkOutOp),
  patchReturnDrive: makeFacade(patchReturnDriveOp),
  patchOdometer: makeFacade(patchOdometerOp),
  voidCallLog: makeFacade(voidCallLogOp),
  reopenVisit: makeFacade(reopenVisitOp),
  deleteTimeEntry: makeFacade(deleteTimeEntryOp),
  editVisit: makeFacade(editVisitOp),
  submitSurvey: makeFacade(submitSurveyOp),
  uploadPhoto: makeFacade(uploadPhotoOp),
  uploadDocument: makeFacade(uploadDocumentOp),
  addStoreNote: makeFacade(addStoreNoteOp),
  addStoreManager: makeFacade(addStoreManagerOp),
  createOrder: makeFacade(createOrderOp),
  createManualVisit: makeFacade(createManualVisitOp),
  patchStoreCoordinates: makeFacade(patchStoreCoordinatesOp),
  uploadProfileSignature: makeFacade(uploadProfileSignatureOp),
};
